/**
 * Custom dropdown that replaces the native <select>.
 *
 * - 40px trigger matches the standard Input height so it lines up in form rows.
 * - Boxed-chevron slot mirrors the design library's .select-wrap aesthetic.
 * - Popover menu uses .menu-pop styling with a small scale/fade entry.
 * - Keyboard: ↓/Space/Enter to open, ↑↓ to move, Enter to commit, Esc to close.
 * - Closes on outside click and on Escape.
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Field } from './Field';
import { Icon } from './Icon';

export interface DropdownOption {
  value: string;
  label: string;
  hint?: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
  label?: string;
  hint?: string;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  ariaLabel?: string;
}

export function DropdownSelect({
  value, onChange, options,
  label, hint, error,
  placeholder = 'Sélectionner…',
  disabled, className, id, ariaLabel,
}: Props) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const [open, setOpen] = useState(false);
  const [focusIdx, setFocusIdx] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selected = useMemo(() => options.find((o) => o.value === value), [options, value]);

  // Close on outside mousedown.
  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  // Reset focus when opening.
  useEffect(() => {
    if (open) {
      const idx = options.findIndex((o) => o.value === value);
      setFocusIdx(idx >= 0 ? idx : 0);
    }
  }, [open, options, value]);

  function commit(idx: number): void {
    const opt = options[idx];
    if (!opt) return;
    onChange(opt.value);
    setOpen(false);
    triggerRef.current?.focus();
  }

  function handleKey(e: KeyboardEvent<HTMLDivElement>): void {
    if (disabled) return;
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'Escape')    { e.preventDefault(); setOpen(false); triggerRef.current?.focus(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setFocusIdx((i) => Math.min(options.length - 1, i + 1)); return; }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setFocusIdx((i) => Math.max(0, i - 1)); return; }
    if (e.key === 'Home')      { e.preventDefault(); setFocusIdx(0); return; }
    if (e.key === 'End')       { e.preventDefault(); setFocusIdx(options.length - 1); return; }
    if (e.key === 'Enter')     { e.preventDefault(); if (focusIdx >= 0) commit(focusIdx); return; }
    if (e.key === 'Tab')       { setOpen(false); return; }
  }

  return (
    <Field label={label} hint={hint} error={error} htmlFor={inputId}>
      <div
        ref={containerRef}
        className={`dropdown-select${open ? ' is-open' : ''}${error ? ' err-state' : ''} ${className ?? ''}`.trim()}
        onKeyDown={handleKey}
      >
        <button
          ref={triggerRef}
          id={inputId}
          type="button"
          className="select-trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={ariaLabel ?? label}
          disabled={disabled}
          onClick={() => !disabled && setOpen((o) => !o)}
        >
          <span className={`select-label${selected ? '' : ' placeholder'}`}>
            {selected ? selected.label : placeholder}
          </span>
          <span className={`chev-slot${open ? ' open' : ''}`} aria-hidden>
            <Icon name="chevron-down" size={14} />
          </span>
        </button>

        {open && (
          <div className="menu-pop dropdown-menu" role="listbox" tabIndex={-1}>
            {options.map((opt, i) => (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={opt.value === value}
                className={`menu-option${i === focusIdx ? ' focused' : ''}${opt.value === value ? ' selected' : ''}`}
                onMouseEnter={() => setFocusIdx(i)}
                onClick={() => commit(i)}
              >
                <span className="opt-text">
                  <span className="opt-label">{opt.label}</span>
                  {opt.hint && <span className="opt-hint">{opt.hint}</span>}
                </span>
                {opt.value === value && <Icon name="check" size={14} />}
              </button>
            ))}
          </div>
        )}
      </div>
    </Field>
  );
}
